import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { EntityState, serializeAxiosError, IQueryParams } from 'app/shared/reducers/reducer.utils';
import { IIndicatorDto, defaultValue } from 'app/shared/model/indicator-dto.model';
import { IKpiResponse, IKpiTimeResponse } from 'app/shared/model/indicator-dto';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';

const initialState: EntityState<IIndicatorDto> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

export interface IAlertsProps extends IQueryParams {
  sensor: string | null;
  periodicType: PeriodicType | null;
  truckImplementId?: number | string;
  companyId?: number | null;
  dateStart?: string | null;
  dateEnd?: string | null;
}

const apiUrl = '/api/indicator-count';

// Actions

export const getAlertsPerTime = createAsyncThunk(
  'indicatorAlerts/fetch_alerts_per_time_periodic',
  async ({ sensor, periodicType, truckImplementId, companyId, dateStart, dateEnd }: IAlertsProps) => {
    const apiUrlComplete = `${apiUrl}/time/${sensor}${truckImplementId ? `/${truckImplementId}` : ''}/${periodicType}${
      companyId ? `?searchCompanyId=${companyId}` : ''
    }`;
    const params = {
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiTimeResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// export const getFenceAlertsPerTime = createAsyncThunk(
//   'indicatorAlerts/fetch_fence_alerts_per_time_periodic',
//   async ({ sensor, periodicType, truckImplementId, companyId, dateStart, dateEnd }: IAlertsProps ) => {
//     const apiUrlComplete = `${apiUrl}/time/${sensor}${truckImplementId ? `/${truckImplementId}` : ''}/${periodicType}${companyId ? `?searchCompanyId=${companyId}` : ''}`;
//     const params = {
//       dateStart,
//       dateEnd,
//     };
//     const response = await axios.get<IKpiTimeResponse>(apiUrlComplete, {params});
//     return response.data
//   },
//   { serializeError: serializeAxiosError }
// );

// export const getIdentificationAlertsPerTime = createAsyncThunk(
//   'indicatorAlerts/fetch_identification_alerts_per_time_periodic',
//   async ({ sensor, periodicType, truckImplementId, companyId, dateStart, dateEnd }: IAlertsProps ) => {
//     const apiUrlComplete = `${apiUrl}/time/${sensor}${truckImplementId ? `/${truckImplementId}` : ''}/${periodicType}${companyId ? `?searchCompanyId=${companyId}` : ''}`;
//     const params = {
//       dateStart,
//       dateEnd,
//     };
//     const response = await axios.get<IKpiTimeResponse>(apiUrlComplete, {params});
//     return response.data
//   },
//   { serializeError: serializeAxiosError }
// );

export const getAlertsPerVehicleAndPlate = createAsyncThunk(
  'indicatorAlerts/fetch_alerts_per_vehicle_and_plate_periodic',
  async ({ sensor, periodicType, companyId, dateStart, dateEnd }: IAlertsProps) => {
    const apiUrlComplete = `${apiUrl}/statistics/${sensor}/${periodicType}${companyId ? `?searchCompanyId=${companyId}` : ''}`;
    const params = {
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getFenceAlertsPerVehicleAndPlate = createAsyncThunk(
  'indicatorAlerts/fetch_fence_alerts_per_vehicle_and_plate_periodic',
  async ({ sensor, periodicType, companyId, dateStart, dateEnd }: IAlertsProps) => {
    const apiUrlComplete = `${apiUrl}/statistics/${sensor}/${periodicType}${companyId ? `?searchCompanyId=${companyId}` : ''}`;
    const params = {
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getIdentificationAlertsPerVehicleAndPlate = createAsyncThunk(
  'indicatorAlerts/fetch_identification_alerts_per_vehicle_and_plate_periodic',
  async ({ sensor, periodicType, companyId, dateStart, dateEnd }: IAlertsProps) => {
    const apiUrlComplete = `${apiUrl}/statistics/${sensor}/${periodicType}${companyId ? `?searchCompanyId=${companyId}` : ''}`;
    const params = {
      dateStart,
      dateEnd,
    };
    const response = await axios.get<IKpiResponse>(apiUrlComplete, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// Slice

const indicatorAlertsSlice = createSlice({
  name: 'indicatorAlerts',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAlertsPerTime.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerTime = action.payload;
      })
      .addCase(getAlertsPerTime.pending, state => {
        state.loading = true;
      })
      .addCase(getAlertsPerTime.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      // .addCase(getFenceAlertsPerTime.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.entity.kpiPerTime = action.payload;
      // })
      // .addCase(getFenceAlertsPerTime.pending, state => {
      //   state.loading = true;
      // })
      // .addCase(getFenceAlertsPerTime.rejected, (state, action) => {
      //   state.loading = false;
      //   state.errorMessage = action.error.message || 'Error fetching data';
      // })
      // .addCase(getIdentificationAlertsPerTime.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.entity.kpiPerTime = action.payload;
      // })
      // .addCase(getIdentificationAlertsPerTime.pending, state => {
      //   state.loading = true;
      // })
      // .addCase(getIdentificationAlertsPerTime.rejected, (state, action) => {
      //   state.loading = false;
      //   state.errorMessage = action.error.message || 'Error fetching data';
      // })
      .addCase(getAlertsPerVehicleAndPlate.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerVehicleAndStatistics = action.payload;
      })
      .addCase(getAlertsPerVehicleAndPlate.pending, state => {
        state.loading = true;
      })
      .addCase(getAlertsPerVehicleAndPlate.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addCase(getFenceAlertsPerVehicleAndPlate.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerVehicleAndStatistics = action.payload;
      })
      .addCase(getFenceAlertsPerVehicleAndPlate.pending, state => {
        state.loading = true;
      })
      .addCase(getFenceAlertsPerVehicleAndPlate.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addCase(getIdentificationAlertsPerVehicleAndPlate.fulfilled, (state, action) => {
        state.loading = false;
        state.entity.kpiPerVehicleAndStatistics = action.payload;
      })
      .addCase(getIdentificationAlertsPerVehicleAndPlate.pending, state => {
        state.loading = true;
      })
      .addCase(getIdentificationAlertsPerVehicleAndPlate.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      });
  },
});

export const { reset } = indicatorAlertsSlice.actions;

export default indicatorAlertsSlice.reducer;
